import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  hubService,
  type DomainProfile,
  type CreateDomainProfileDto,
} from '../../../services/hub.service';
import { AiConfigTabShell, AiConfigTable } from '../index';

export function DomainProfilesTab() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<DomainProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DomainProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateDomainProfileDto>({
    domain_id: '',
    label: { mn: '', en: '' },
    example_phrases: [],
    is_default: false,
  });

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listDomainProfiles(false, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.domain_id.trim()) return;
    try {
      const token = getToken();
      await hubService.createDomainProfile(
        { ...form, example_phrases: form.example_phrases.filter(Boolean) },
        token,
      );
      setCreating(false);
      setForm({ domain_id: '', label: { mn: '', en: '' }, example_phrases: [], is_default: false });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleUpdate(id: string) {
    if (!editing) return;
    try {
      const token = getToken();
      await hubService.updateDomainProfile(
        id,
        {
          label: editing.label,
          example_phrases: editing.example_phrases,
          is_default: editing.is_default,
          is_active: editing.is_active,
        },
        token,
      );
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this domain profile?')) return;
    try {
      const token = getToken();
      await hubService.deleteDomainProfile(id, token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <AiConfigTabShell loading={loading} error={error}>
      <div className="space-y-6">
        {!creating ? (
          <button
            onClick={() => setCreating(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Add Domain Profile
          </button>
        ) : (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <input
              placeholder="domain_id (e.g. domain.information)"
              value={form.domain_id}
              onChange={(e) => setForm((f) => ({ ...f, domain_id: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Label MN"
              value={form.label.mn}
              onChange={(e) => setForm((f) => ({ ...f, label: { ...f.label, mn: e.target.value } }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Example phrases (comma-separated)"
              value={form.example_phrases.join(', ')}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  example_phrases: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_default === true}
                onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
              />
              Default (always available for every bot)
            </label>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                Save
              </button>
              <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                Cancel
              </button>
            </div>
          </div>
        )}

        <AiConfigTable>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">domain_id</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm font-mono">{item.domain_id}</td>
                <td className="px-4 py-2 text-sm">{item.label?.mn || item.label?.en || '-'}</td>
                <td className="px-4 py-2 text-sm">{item.is_default ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 text-sm">{item.is_active !== false ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(item)}
                    className="text-blue-600 hover:underline text-sm mr-2"
                  >
                    Edit
                  </button>
                  {!item.is_system && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </AiConfigTable>

        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full space-y-3">
              <h3 className="font-semibold">Edit {editing.domain_id}</h3>
              <input
                placeholder="Label MN"
                value={editing.label?.mn ?? ''}
                onChange={(e) =>
                  setEditing((x) =>
                    x ? { ...x, label: { ...x.label, mn: e.target.value } } : null,
                  )
                }
                className="w-full rounded border px-3 py-2 text-sm"
              />
              <textarea
                placeholder="Example phrases (comma-separated)"
                value={(editing.example_phrases || []).join(', ')}
                onChange={(e) =>
                  setEditing((x) =>
                    x
                      ? {
                          ...x,
                          example_phrases: e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean),
                        }
                      : null,
                  )
                }
                className="w-full rounded border px-3 py-2 text-sm"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.is_default === true}
                  onChange={(e) =>
                    setEditing((x) => (x ? { ...x, is_default: e.target.checked } : null))
                  }
                />
                Default (always available for every bot)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editing.is_active !== false}
                  onChange={(e) =>
                    setEditing((x) => (x ? { ...x, is_active: e.target.checked } : null))
                  }
                />
                Active
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => handleUpdate(editing.id)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="px-3 py-1.5 text-sm bg-gray-200 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AiConfigTabShell>
  );
}
